import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Wallet, ChevronDown, ExternalLink, Loader2, Search } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { usePersistedState } from '../../lib/persist.js'
import { nameInitial } from '../../lib/text.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { Card, DateRange, Segmented, Notice, fmt, toISO, parseISO, todayISO, INPUT, TH_MON_FULL } from '../statistic/statShared.jsx'
import { toISODate } from '../../kit/format.js'
import Pager from '../../components/Pager.jsx'

/* -------------------------------------------------------------------------- */
/* รายรับ (revenue) — READ-ONLY range inspection report (pivot + drill-down).  */
/*                                                                            */
/* GROUP HEADERS + TOTALS come from /billing `type=report_total` — an          */
/* aggregate "cube" grouped by (date, branch, doctor_id) over the whole range  */
/* (chunked ≤31 days). We pivot that cube CLIENT-side by the chosen dimension  */
/* (doctor / branch / date), so group totals cover the entire filter with no   */
/* pagination (groups are bounded). The doctor filter list is DERIVED from the */
/* cube — only doctors that actually billed in the current date+branch set.    */
/*                                                                            */
/* BILLING DETAIL within a group is lazy-loaded on expand from /billing        */
/* `type=dashboard` — a standard paginated list — filtered to that group.      */
/* Read-only; "เปิด" opens the patient page in a new tab.                       */
/* -------------------------------------------------------------------------- */

const TEAL = '#0d9488'   // เก็บเอง (priceXunit) — headline / cash collected
const INDIGO = '#6366f1' // เบิก (priceadvance)

// ── money accessors (mirror Treatments.jsx; billing lines share one schema) ──
const num = (v) => { const n = Number(String(v ?? '').replace(/[, ฿]/g, '')); return isNaN(n) ? 0 : n }
const bPrice = (b) => num(b.price ?? b.priceClinic ?? 0)
const bUnit = (b) => { const u = num(b.N ?? b.unit ?? 1); return u || 1 }
const bXunit = (b) => (b.priceXunit != null ? num(b.priceXunit) : bPrice(b) * bUnit(b)) // เก็บเอง
const bAdvance = (b) => num(b.priceadvance ?? b.priceAdvance ?? b.price_advance ?? 0)    // เบิก
const bAdvancePayer = (b) => String(b.advancePay ?? b.advancepay ?? b.advance_pay ?? '').trim()
const bActualClinic = (b) => num(b.priceActualClinic ?? b.priceClinic ?? 0)             // คลินิก
const bDoctor = (b) => num(b.priceDoctor ?? b.pricedoctor ?? b.price_doctor ?? 0)       // หมอ
const bTotal = (b) => {
  const t = b.priceActualTotal ?? b.priceactualtotal ?? b.price_actual_total
  return t != null ? num(t) : bActualClinic(b) + bDoctor(b)                             // รวม
}
const bTypeName = (b) => {
  const bt = b.billing_type ?? b.billingType
  if (bt && typeof bt === 'object') return String(bt.name || bt.type || bt.detail || bt.label || '').trim()
  return String(bt ?? b.name ?? b.detail ?? '').trim() || '—'
}
const bIsActive = (b) => b.active !== false

// NOTE: this inspection report shows the REAL branch name — สนามเป้า stays
// สนามเป้า (NO อารีย์ relabel used elsewhere in the app), so accounting/audit
// figures tie to the actual branch on record.
const stripStars = (s) => String(s ?? '').replace(/^\**|\**$/g, '').trim()
// A doctor is labelled by CODE everywhere; fall back to real name, then id.
const docCode = (d, id) => (d?.code || d?.realname || (id ? `หมอ #${id}` : 'ไม่ระบุหมอ'))
const ZERO = { xunit: 0, advance: 0, total: 0, clinic: 0, doctor: 0 }
const addSums = (a, b) => ({ xunit: a.xunit + b.xunit, advance: a.advance + b.advance, total: a.total + b.total, clinic: a.clinic + b.clinic, doctor: a.doctor + b.doctor })

// ── report_total cube (aggregate) ────────────────────────────────────────────
// report_total groups by (date_label, branch, doctor_id) with sums:
//   price_Total=เก็บเอง · price_Advance=เบิก · price_actualTotal=รวม
//   price_actualClinic=คลินิก · price_Doctor=หมอ
function normDay(v) {
  if (v == null || v === '') return ''
  // epoch → the Bangkok day (safe for both BKK- and UTC-midnight epochs), not the browser's
  if (typeof v === 'number' || /^\d{10,}$/.test(String(v))) return toISODate(v) || String(v)
  return String(v).slice(0, 10)
}
function normalizeCube(rows) {
  return asList(rows).map((r) => ({
    day: normDay(r.date_label ?? r.date),
    branchId: String(r.branch ?? r.branch_id ?? ''),
    doctorId: String(r.doctor_id ?? ''),
    xunit: num(r.price_Total), advance: num(r.price_Advance),
    total: num(r.price_actualTotal), clinic: num(r.price_actualClinic), doctor: num(r.price_Doctor),
  }))
}
// roll the cube up by a single dimension → [{ key, ...sums }]
function pivotCube(cube, dim) {
  const keyOf = dim === 'doctor' ? (c) => c.doctorId : dim === 'branch' ? (c) => c.branchId : (c) => c.day
  const m = new Map()
  for (const c of cube) {
    const k = keyOf(c)
    m.set(k, addSums(m.get(k) || ZERO, c))
  }
  return [...m.entries()].map(([key, s]) => ({ key, ...s }))
}

// ── billing-line → treatment-card adapter (dashboard detail) ─────────────────
function lineJoin(b, branchById, doctorById) {
  const branchId = b.branch_id ?? null
  const doctorId = b.doctor_id ?? null
  const doc = doctorById?.get(String(doctorId)) || null
  return {
    treatmentId: b.treatment_id ?? null,
    patientId: b.patient_id ?? null,
    patientName: stripStars(b.fullname ?? '') || '—',
    hn: b.HN ?? '',
    date: String(b.treatment_date ?? b.date ?? '').slice(0, 10),
    branchId,
    branchName: (branchById?.get(String(branchId)) || '') || (branchId != null ? `สาขา #${branchId}` : '—'),
    doctorName: stripStars(doc?.code || b.doctor?.code || doc?.realname || '') || '—',
    doctorImg: doc?.img || b.doctor?.profile_img || '',
    payment: paymentName(b),
    paymentColor: b.payment_color || null,
  }
}
function paymentName(b) {
  const raw = b.payment_method ?? b.paymentMethod ?? b.payment
  if (raw && typeof raw === 'object') return String(raw.name || raw.label || raw.type || '').trim() || 'ไม่ระบุ'
  return String(raw ?? '').trim() || 'ไม่ระบุ'
}
// Group a page of billing lines into treatment cards (keyed by treatment_id).
function groupTreatments(lines, branchById, doctorById) {
  const order = []
  const byTx = new Map()
  for (const b of lines) {
    if (!bIsActive(b)) continue
    const j = lineJoin(b, branchById, doctorById)
    const key = String(j.treatmentId ?? `nk-${order.length}`)
    if (!byTx.has(key)) { byTx.set(key, { key, ...j, lines: [] }); order.push(key) }
    byTx.get(key).lines.push(b)
  }
  return order.map((k) => {
    const g = byTx.get(k)
    const sums = g.lines.reduce((a, b) => addSums(a, {
      xunit: bXunit(b), advance: bAdvance(b), clinic: bActualClinic(b), doctor: bDoctor(b), total: bTotal(b),
    }), ZERO)
    return { ...g, sums }
  })
}

// Split [startISO, endExclusiveISO) into consecutive ≤maxDays windows (report_total ≤31d).
function chunkRange(startISO, endExclusiveISO, maxDays = 30) {
  const out = []
  let s = parseISO(startISO)
  const end = parseISO(endExclusiveISO)
  while (s < end && out.length < 60) {
    let e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + maxDays)
    if (e > end) e = end
    out.push([toISO(s), toISO(e)])
    s = e
  }
  return out
}
const addDay = (iso) => { const d = parseISO(iso); return toISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) }

/* ── small presentational bits ─────────────────────────────────────────────── */
function Select({ value, onChange, children, className = '' }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} ${className}`}>{children}</select>
}

// Single-select typeahead combobox. options: [{ id, name, code?, img?, search? }]; '' selects allLabel.
function SearchSelect({ value, onChange, options, allLabel = 'ทั้งหมด', placeholder = 'ค้นหา…', className = '' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const inputRef = useRef(null)
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus() }, [open])
  const selected = options.find((o) => o.id === value)
  const label = value === '' ? allLabel : (selected?.name || value)
  const needle = q.trim().toLowerCase()
  const filtered = needle ? options.filter((o) => (o.search || o.name).toLowerCase().includes(needle)) : options
  const pick = (id) => { onChange(id); setOpen(false); setQ('') }
  // Keyboard nav: the always-present "all" row is a real selectable option at
  // index 0; filtered options follow at index i + 1.
  const ta = useTypeahead({
    count: filtered.length + 1,
    onSelect: (i) => { if (i === 0) pick(''); else pick(filtered[i - 1].id) },
    onClose: () => setOpen(false),
  })
  return (
    <div className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={`${INPUT} inline-flex w-full items-center justify-between gap-2 text-left`}>
        <span className={`truncate ${value === '' ? 'text-slate-500' : 'text-slate-700'}`}>{label}</span>
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden tabIndex={-1} />
          <div className="absolute z-20 mt-1 w-full min-w-[220px] rounded-xl border border-slate-200 bg-white shadow-lg p-2">
            <div className="flex items-center gap-1.5 mb-1.5 rounded-md border border-slate-200 px-2 focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-100">
              <Search size={13} className="text-slate-400 shrink-0" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={ta.onKeyDown} placeholder={placeholder}
                className="w-full py-1 text-[12.5px] bg-transparent focus:outline-none" />
            </div>
            <div ref={ta.listRef} className="max-h-60 overflow-y-auto -mr-1 pr-1">
              <button onClick={() => pick('')} data-ti-index={0} onMouseEnter={() => ta.setActiveIndex(0)}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${0 === ta.activeIndex ? 'bg-slate-100' : ''} ${value === '' ? 'text-brand-600 font-medium' : 'text-slate-600'}`}>
                {allLabel}
              </button>
              {filtered.map((o, i) => (
                <button key={o.id} onClick={() => pick(o.id)} data-ti-index={i + 1} onMouseEnter={() => ta.setActiveIndex(i + 1)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${i + 1 === ta.activeIndex ? 'bg-slate-100' : ''} ${o.id === value ? 'text-brand-600 font-medium' : 'text-slate-700'}`}>
                  {o.img
                    ? <img src={o.img} alt="" className="w-5 h-5 rounded-full object-cover shrink-0 bg-slate-100" />
                    : <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[10px] font-semibold shrink-0">{nameInitial(o.name)}</span>}
                  <span className="truncate">{o.name}</span>
                  {o.sub ? <span className="ml-auto text-[11px] text-slate-400 shrink-0 truncate max-w-[45%]">{o.sub}</span> : null}
                </button>
              ))}
              {!filtered.length && <div className="px-2 py-3 text-center text-[12px] text-slate-400">ไม่พบ</div>}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
function Money({ v, color, strong }) {
  return <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`} style={color ? { color } : undefined}>{v ? fmt(v) : '—'}</span>
}
function PaymentBadge({ name, color }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-50 border border-slate-200 px-1.5 py-0.5 text-[11.5px] text-slate-600">
      {color && <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />}{name}
    </span>
  )
}
function DoctorAvatar({ src, name }) {
  const [err, setErr] = useState(false)
  if (src && !err) return <img src={src} alt="" onError={() => setErr(true)} className="w-6 h-6 rounded-full object-cover shrink-0 bg-slate-100 border border-slate-200" />
  return <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{nameInitial(name)}</span>
}
function SumTile({ label, value, color, strong }) {
  return (
    <div className="min-w-[92px]">
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-500">
        {color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}{label}
      </div>
      <div className={`tabular-nums leading-tight ${strong ? 'text-[18px] font-semibold text-slate-900' : 'text-[15px] font-medium text-slate-700'}`}>{fmt(value)}</div>
    </div>
  )
}
function fmtDate(iso) {
  if (!iso) return '—'
  const d = parseISO(iso)
  if (isNaN(d)) return iso
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`
}
function fmtDateFull(iso) {
  const d = parseISO(iso)
  if (isNaN(d)) return iso
  return `${d.getDate()} ${TH_MON_FULL[d.getMonth()]} ${d.getFullYear() + 543}`
}

/* ── billing lines (inside a treatment card) ───────────────────────────────── */
function BillingLines({ lines }) {
  const sorted = [...lines].sort((a, b) => bTotal(b) - bTotal(a))
  return (
    <table className="w-full text-[12.5px] table-fixed">
      <colgroup><col /><col className="w-[64px]" /><col className="w-[92px]" /><col className="w-[130px]" /><col className="w-[92px]" /><col className="w-[80px]" /><col className="w-[92px]" /></colgroup>
      <thead>
        <tr className="text-[11px] text-slate-400 border-b border-slate-100">
          <th className="text-left font-medium py-1">รายการ</th>
          <th className="text-right font-medium">จำนวน</th>
          <th className="text-right font-medium" style={{ color: TEAL }}>รวม</th>
          <th className="text-right font-medium">เบิก</th>
          <th className="text-right font-medium">คลินิก</th>
          <th className="text-right font-medium">หมอ</th>
          <th className="text-right font-medium">รวมเบิก</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((b, i) => {
          const payer = bAdvancePayer(b)
          return (
            <tr key={b.id ?? i} className="border-b border-slate-50 last:border-0">
              <td className="py-1 pr-2 text-slate-700 truncate">{bTypeName(b)}</td>
              <td className="text-right text-slate-500 tabular-nums" title={`ราคา/หน่วย ${fmt(bPrice(b))}`}>{bUnit(b)}</td>
              <td className="text-right"><Money v={bXunit(b)} color={TEAL} /></td>
              <td className="text-right">
                <Money v={bAdvance(b)} color={INDIGO} />
                {payer ? <span className="block text-[10.5px] text-slate-400 truncate">{payer}</span> : null}
              </td>
              <td className="text-right"><Money v={bActualClinic(b)} /></td>
              <td className="text-right"><Money v={bDoctor(b)} /></td>
              <td className="text-right"><Money v={bTotal(b)} strong /></td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/* ── treatment-grain table (used inside each expanded group) ───────────────── */
function TreatmentList({ groups, onOpenPatient }) {
  const [openTx, setOpenTx] = useState(() => new Set())
  const toggle = (k) => setOpenTx((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px] table-fixed">
        <colgroup>
          <col className="w-[30px]" /><col className="w-[78px]" /><col /><col className="w-[110px]" />
          <col className="w-[104px]" /><col className="w-[118px]" />
          <col className="w-[90px]" /><col className="w-[78px]" /><col className="w-[90px]" /><col className="w-[58px]" />
        </colgroup>
        <thead>
          <tr className="text-[11.5px] text-slate-400 border-b border-slate-100">
            <th></th>
            <th className="text-left font-medium py-1.5">วันที่</th>
            <th className="text-left font-medium">คนไข้</th>
            <th className="text-left font-medium">สาขา</th>
            <th className="text-left font-medium">หมอ</th>
            <th className="text-left font-medium">ชำระ</th>
            <th className="text-right font-medium" style={{ color: TEAL }}>รวม</th>
            <th className="text-right font-medium">เบิก</th>
            <th className="text-right font-medium">รวมเบิก</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const open = openTx.has(g.key)
            return (
              <Fragment key={g.key}>
                <tr className="border-b border-slate-50 hover:bg-white cursor-pointer" onClick={() => toggle(g.key)}>
                  <td className="py-2 text-slate-300"><ChevronDown size={15} className={`transition ${open ? '' : '-rotate-90'}`} /></td>
                  <td className="text-slate-600 tabular-nums">{fmtDate(g.date)}</td>
                  <td className="pr-2 truncate">
                    <span className="text-slate-800">{g.patientName || '—'}</span>
                    {g.hn ? <span className="text-slate-400 ml-1.5 text-[11.5px]">HN {g.hn}</span> : null}
                  </td>
                  <td className="text-slate-500 truncate pr-2">{g.branchName || '—'}</td>
                  <td className="pr-2"><span className="inline-flex items-center gap-1.5 min-w-0"><DoctorAvatar src={g.doctorImg} name={g.doctorName} /><span className="truncate text-slate-600">{g.doctorName || '—'}</span></span></td>
                  <td><PaymentBadge name={g.payment} color={g.paymentColor} /></td>
                  <td className="text-right"><Money v={g.sums.xunit} color={TEAL} strong /></td>
                  <td className="text-right"><Money v={g.sums.advance} color={INDIGO} /></td>
                  <td className="text-right"><Money v={g.sums.total} strong /></td>
                  <td className="text-right">
                    {g.patientId ? (
                      <button onClick={(e) => { e.stopPropagation(); onOpenPatient(g.patientId) }} title="เปิดหน้าคนไข้"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-500 hover:border-brand-300 hover:text-brand-600">
                        <ExternalLink size={12} />เปิด
                      </button>
                    ) : null}
                  </td>
                </tr>
                {open && (
                  <tr className="bg-white/70">
                    <td></td>
                    <td colSpan={9} className="py-2 pr-2"><BillingLines lines={g.lines} /></td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ── group detail (lazy dashboard fetch for one group, paginated) ──────────── */
function GroupDetail({ startDate, endDate, branchId, doctorId, perPage, branchById, doctorById, onOpenPatient }) {
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [st, setSt] = useState({ loading: true, err: '' })
  const reqId = useRef(0)
  useEffect(() => { setPage(1) }, [startDate, endDate, branchId, doctorId, perPage])
  useEffect(() => {
    const id = ++reqId.current
    setSt({ loading: true, err: '' })
    apiGet('/api/billing', { type: 'dashboard', start_date: startDate, end_date: endDate, branch_id: branchId, doctor_id: doctorId, page, per_page: perPage })
      .then((r) => {
        if (id !== reqId.current) return
        const lines = asList(r)
        const itemsTotal = Number(r?.itemsTotal ?? r?.total ?? lines.length) || lines.length
        const pageTotal = Number(r?.pageTotal ?? Math.max(1, Math.ceil(itemsTotal / Number(perPage)))) || 1
        setData({ lines, itemsTotal, pageTotal })
        setSt({ loading: false, err: '' })
      })
      .catch((e) => { if (id === reqId.current) setSt({ loading: false, err: String(e.message || e) }) })
  }, [startDate, endDate, branchId, doctorId, page, perPage])

  const groups = useMemo(() => (data ? groupTreatments(data.lines, branchById, doctorById) : []), [data, branchById, doctorById])
  if (st.err) return <div className="px-3 py-4 text-[12.5px] text-rose-600">โหลดรายการไม่สำเร็จ: {st.err}</div>
  if (st.loading) return <div className="flex items-center gap-2 px-3 py-5 text-[12.5px] text-slate-400"><Loader2 size={14} className="animate-spin" /> กำลังโหลดรายการ…</div>
  if (!groups.length) return <div className="px-3 py-4 text-[12.5px] text-slate-400">ไม่มีรายการ</div>
  const pageTotal = data.pageTotal, itemsTotal = data.itemsTotal
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-2">
      <TreatmentList groups={groups} onOpenPatient={onOpenPatient} />
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <div className="text-[12px] text-slate-500">{fmt(itemsTotal)} รายการ · หน้า {page}/{pageTotal}</div>
        <Pager page={page} pageTotal={pageTotal} onChange={setPage} size="sm" />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* page                                                                       */
/* -------------------------------------------------------------------------- */
const GROUP_OPTS = [{ v: 'doctor', label: 'หมอ' }, { v: 'branch', label: 'สาขา' }, { v: 'date', label: 'วันที่' }]
const GROUP_NOUN = { doctor: 'หมอ', branch: 'สาขา', date: 'วัน' }

export default function RevenueReport() {
  const today = useMemo(() => todayISO(), [])   // Bangkok, not the browser clock
  const monthStart = useMemo(() => { const d = parseISO(today); return toISO(new Date(d.getFullYear(), d.getMonth(), 1)) }, [today])

  const [start, setStart] = usePersistedState('rev:start', monthStart)
  const [end, setEnd] = usePersistedState('rev:end', today)
  const [branchId, setBranchId] = usePersistedState('rev:branch', '')
  const [doctorId, setDoctorId] = usePersistedState('rev:doctor', '')
  const [groupBy, setGroupBy] = usePersistedState('rev:groupBy', 'doctor') // doctor | branch | date
  const [perPage, setPerPage] = usePersistedState('rev:perPage', '100')    // billing rows per page WITHIN a group
  // legacy stored '0' meant "all"; the '==?' backend needs it OMITTED → coerce to ''
  const branchSel = branchId === '0' ? '' : branchId
  const doctorSel = doctorId === '0' ? '' : doctorId

  const [branchList, setBranchList] = useState([])
  const [doctorMap, setDoctorMap] = useState(() => new Map()) // id → { id, name, code, img, name_eng }
  const branchById = useMemo(() => new Map(branchList.map((b) => [b.id, b.name])), [branchList])
  const doctorById = doctorMap

  const [cube, setCube] = useState(null) // normalized rows | null
  const [cubeState, setCubeState] = useState({ loading: true, err: '' })
  const [openGroup, setOpenGroup] = useState(null) // one group open at a time

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranchList(asList(r).map((b) => ({ id: String(b.id ?? b.branch_id), name: b.name || b.branch_name || `สาขา #${b.id}` })))).catch(() => {})
    cachedGet('/api/doctors').then((r) => {
      const m = new Map()
      for (const d of asList(r)) {
        const id = String(d.id ?? d.doctor_id)
        // `code` is the doctor's display label across the app (e.g. AppointmentPrint);
        // the real name = name+surname, kept for the muted subtitle + search.
        const code = stripStars(d.code || '')
        const realname = stripStars([d.name, d.surname].filter(Boolean).join(' ') || d.nickname || d.name_eng || '')
        m.set(id, { id, code, realname, img: d.profile_img || d.image || '', name_eng: d.name_eng || '' })
      }
      setDoctorMap(m)
    }).catch(() => {})
  }, [])

  // Xano `billing.date < end_date` is exclusive → send end+1 to include the end day.
  const endExclusive = useMemo(() => addDay(end), [end])

  // aggregate cube — filtered by date + BRANCH only (NOT doctor, so the doctor
  // dropdown stays populated); doctor is applied client-side below.
  const cubeReq = useRef(0)
  useEffect(() => {
    const id = ++cubeReq.current
    setCubeState({ loading: true, err: '' })
    setOpenGroup(null)
    Promise.all(chunkRange(start, endExclusive).map(([s, e]) =>
      apiGet('/api/billing', { type: 'report_total', start_date: s, end_date: e, branch_id: branchSel }).then(normalizeCube),
    ))
      .then((res) => { if (id === cubeReq.current) { setCube(res.flat()); setCubeState({ loading: false, err: '' }) } })
      .catch((e) => { if (id === cubeReq.current) { setCube(null); setCubeState({ loading: false, err: String(e.message || e) }) } })
  }, [start, endExclusive, branchSel])

  // collapse any open group when the grouping dimension or doctor filter changes
  // (keys aren't comparable across dimensions — a doctor id could equal a branch id)
  useEffect(() => { setOpenGroup(null) }, [groupBy, doctorSel])

  // doctor client-side filter for totals/groups; NOT for the dropdown options
  const cubeF = useMemo(() => (!cube ? null : (doctorSel ? cube.filter((c) => c.doctorId === doctorSel) : cube)), [cube, doctorSel])
  const grand = useMemo(() => (cubeF || []).reduce((a, c) => addSums(a, c), ZERO), [cubeF])

  // doctor filter options = ONLY doctors present in the current date+branch cube
  const doctorOptions = useMemo(() => {
    if (!cube) return []
    const ids = [...new Set(cube.map((c) => c.doctorId).filter(Boolean))]
    return ids.map((id) => {
      const d = doctorById.get(id)
      const name = docCode(d, id) // display = code
      return { id, name, sub: d?.realname && d.realname !== name ? d.realname : '', img: d?.img || '', search: [name, d?.realname, d?.name_eng, id].filter(Boolean).join(' ').toLowerCase() }
    }).sort((a, b) => a.name.localeCompare(b.name, 'th'))
  }, [cube, doctorById])

  // pivot the (doctor-filtered) cube by the chosen dimension
  const groupRows = useMemo(() => {
    if (!cubeF) return []
    const rows = pivotCube(cubeF, groupBy)
    return groupBy === 'date' ? rows.sort((a, b) => a.key.localeCompare(b.key)) : rows.sort((a, b) => b.xunit - a.xunit)
  }, [cubeF, groupBy])

  const openPatient = (pid) => { if (pid) window.open(`/patient/${pid}`, '_blank', 'noopener') }
  const groupLabel = (row) => {
    if (groupBy === 'doctor') { const d = doctorById.get(row.key); const name = docCode(d, row.key); return { name, img: d?.img, sub: d?.realname && d.realname !== name ? d.realname : '' } }
    if (groupBy === 'branch') return { name: branchById.get(row.key) || (row.key ? `สาขา #${row.key}` : 'ไม่ระบุสาขา') }
    return { name: fmtDateFull(row.key) }
  }
  // dashboard params for a group's detail = base filter + the group's own dimension
  const detailParams = (row) => ({
    startDate: groupBy === 'date' ? row.key : start,
    endDate: groupBy === 'date' ? addDay(row.key) : endExclusive,
    branchId: groupBy === 'branch' ? row.key : branchSel,
    doctorId: groupBy === 'doctor' ? row.key : doctorSel,
  })

  return (
    <div className="px-8 py-6 max-w-[1180px]">
      {/* header */}
      <div className="flex items-center gap-2 text-[13px] text-slate-400 mb-1">
        <Link to="/reports" className="hover:text-brand-600">รายงาน</Link><span>/</span><span className="text-slate-600">รายรับ</span>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><Wallet size={22} strokeWidth={1.75} /></div>
        <div>
          <h1 className="text-[21px] font-semibold text-slate-900 tracking-tight leading-tight">รายรับ</h1>
          <p className="text-[13px] text-slate-500">ตรวจสอบรายรับตามช่วงวันที่ · อ่านอย่างเดียว (ไม่แก้ไขข้อมูลย้อนหลัง)</p>
        </div>
      </div>

      {/* filter bar */}
      <Card icon={<Search size={18} />} title="ค้นหา / กรอง" subtitle="ช่วงวันที่ · สาขา · หมอ · จัดกลุ่มตาม">
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ช่วงวันที่</label>
            <DateRange start={start} end={end} today={today} onStart={setStart} onEnd={setEnd} />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">สาขา</label>
            <Select value={branchSel} onChange={setBranchId} className="min-w-[140px]">
              <option value="">ทุกสาขา</option>
              {branchList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">หมอ <span className="text-slate-300">({doctorOptions.length})</span></label>
            <SearchSelect value={doctorSel} onChange={setDoctorId} options={doctorOptions} allLabel="ทุกหมอ" placeholder="ค้นหาชื่อ/โค้ดหมอ…" className="w-[200px]" />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">จัดกลุ่มตาม</label>
            <Segmented value={groupBy} onChange={setGroupBy} options={GROUP_OPTS} />
          </div>
        </div>
      </Card>

      {/* summary + grouped table */}
      <div className="mt-4">
        <Card
          icon={<Wallet size={18} />}
          title={`รายรับแยกตาม${GROUP_NOUN[groupBy]}`}
          badge={groupRows.length ? `${fmt(groupRows.length)} ${GROUP_NOUN[groupBy]}` : undefined}
          subtitle="รวม = เงินที่เก็บจากคนไข้ · เบิก = ปกส./ประกัน · รวมเบิก = รายรับทั้งหมด · กดที่แถวเพื่อดูรายการ"
          right={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2">
              {cubeState.loading ? (
                <div className="flex items-center gap-2 text-[12.5px] text-slate-400 py-1.5"><Loader2 size={14} className="animate-spin" /> รวมทั้งช่วง…</div>
              ) : (
                <>
                  <SumTile label="รวม (ทั้งช่วง)" value={grand.xunit} color={TEAL} strong />
                  <SumTile label="เบิก" value={grand.advance} color={INDIGO} />
                  <SumTile label="รวมเบิก" value={grand.total} />
                  <SumTile label="คลินิก" value={grand.clinic} />
                  <SumTile label="หมอ" value={grand.doctor} />
                </>
              )}
            </div>
          }
        >
          {cubeState.err ? (
            <Notice tone="error" text={`โหลดข้อมูลไม่สำเร็จ: ${cubeState.err}`} />
          ) : cubeState.loading ? (
            <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[200px]"><Loader2 size={16} className="animate-spin" /> กำลังโหลด…</div>
          ) : !groupRows.length ? (
            <Notice tone="warn" text="ไม่พบรายการในช่วงและตัวกรองที่เลือก" />
          ) : (
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-[13px] table-fixed">
                <colgroup>
                  <col className="w-[34px]" /><col /><col className="w-[112px]" /><col className="w-[100px]" /><col className="w-[112px]" /><col className="w-[100px]" /><col className="w-[100px]" />
                </colgroup>
                <thead>
                  <tr className="text-[11.5px] text-slate-400 border-b border-slate-200">
                    <th></th>
                    <th className="text-left font-medium py-2">{GROUP_NOUN[groupBy] === 'วัน' ? 'วันที่' : GROUP_NOUN[groupBy]}</th>
                    <th className="text-right font-medium" style={{ color: TEAL }}>รวม</th>
                    <th className="text-right font-medium">เบิก</th>
                    <th className="text-right font-medium">รวมเบิก</th>
                    <th className="text-right font-medium">คลินิก</th>
                    <th className="text-right font-medium">หมอ</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((row) => {
                    const open = openGroup === row.key
                    const lbl = groupLabel(row)
                    const p = detailParams(row)
                    return (
                      <Fragment key={row.key}>
                        <tr className={`border-b border-slate-100 cursor-pointer ${open ? 'bg-brand-50/40' : 'hover:bg-slate-50/70'}`} onClick={() => setOpenGroup(open ? null : row.key)}>
                          <td className="py-2.5 text-slate-300"><ChevronDown size={16} className={`transition ${open ? '' : '-rotate-90'}`} /></td>
                          <td className="pr-2">
                            <span className="inline-flex items-center gap-2 min-w-0">
                              {groupBy === 'doctor' && <DoctorAvatar src={lbl.img} name={lbl.name} />}
                              <span className="truncate font-medium text-slate-800">{lbl.name}</span>
                              {lbl.sub ? <span className="text-[11px] text-slate-400 shrink-0 truncate">{lbl.sub}</span> : null}
                            </span>
                          </td>
                          <td className="text-right"><Money v={row.xunit} color={TEAL} strong /></td>
                          <td className="text-right"><Money v={row.advance} color={INDIGO} /></td>
                          <td className="text-right"><Money v={row.total} strong /></td>
                          <td className="text-right"><Money v={row.clinic} /></td>
                          <td className="text-right"><Money v={row.doctor} /></td>
                        </tr>
                        {open && (
                          <tr>
                            <td></td>
                            <td colSpan={6} className="py-2 pr-1">
                              <GroupDetail {...p} perPage={perPage} branchById={branchById} doctorById={doctorById} onOpenPatient={openPatient} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>

              <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100 text-[12.5px] text-slate-500">
                รายการต่อหน้า (ในกลุ่ม)
                <Select value={perPage} onChange={setPerPage} className="!py-1 w-[72px]">
                  {['50', '100', '200', '300'].map((n) => <option key={n} value={n}>{n}</option>)}
                </Select>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
